/*
 * FractureBundle — shared logic for PDP bundle builder + cart reminder.
 *
 * Public API (window.FractureBundle):
 *   .addItems(variants)           // POST /cart/add.js with items array; resolves with cart JSON
 *   .getCart()                    // GET /cart.js; resolves with cart JSON
 *   .countBundleItems(cart, type) // counts cart items where product_type matches
 *   .computeRemainder(count, size) // {needed, nextMultiple, percent}
 *   .refreshReminders()           // re-renders all [data-cart-bundle-reminder] bars
 *   .openCartDrawer()             // opens the theme's cart drawer (Dawn-compatible)
 *   .on(event, fn) / .off         // simple pub/sub on 'updated'
 */
(function () {
  'use strict';

  var listeners = {};
  var productCache = {};

  function emit(name, payload) {
    (listeners[name] || []).forEach(function (fn) { try { fn(payload); } catch (e) {} });
  }

  function on(name, fn) {
    (listeners[name] = listeners[name] || []).push(fn);
  }
  function off(name, fn) {
    if (!listeners[name]) return;
    listeners[name] = listeners[name].filter(function (x) { return x !== fn; });
  }

  function formatMoney(cents) {
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: cur,
        maximumFractionDigits: (cents % 100 === 0) ? 0 : 2
      });
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }

  function addItems(variants) {
    var items = (variants || []).map(function (v) {
      if (typeof v === 'number') return { id: v, quantity: 1 };
      return { id: v.id || v.variantId, quantity: v.quantity || 1, properties: v.properties };
    });
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw j; });
      return r.json();
    }).then(function (res) {
      return refreshCartSections().then(function () {
        emit('updated', { source: 'addItems' });
        refreshReminders();
        // Notify the theme that cart changed (Dawn listens for this on <cart-items> / <cart-drawer-items>)
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return res;
      });
    });
  }

  function refreshCartSections() {
    var sections = ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text', 'main-cart-items', 'main-cart-footer'];
    // Capture open state before replacing the drawer
    var prevDrawer = document.querySelector('cart-drawer') || document.getElementById('CartDrawer');
    var wasOpen = prevDrawer && (prevDrawer.classList.contains('active') || (prevDrawer.firstElementChild && prevDrawer.firstElementChild.classList.contains('active')));

    return fetch('/?sections=' + sections.join(','))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var parser = new DOMParser();

        if (data['cart-drawer']) {
          var newDoc = parser.parseFromString(data['cart-drawer'], 'text/html');
          var newDrawer = newDoc.querySelector('cart-drawer') || newDoc.getElementById('CartDrawer');
          var curDrawer = document.querySelector('cart-drawer') || document.getElementById('CartDrawer');
          if (curDrawer && newDrawer) {
            curDrawer.replaceWith(newDrawer);
            if (wasOpen && typeof newDrawer.open === 'function') {
              // Re-open the fresh drawer so the cart stays visible after add-from-picker
              setTimeout(function () { newDrawer.open(); }, 0);
            } else if (wasOpen) {
              newDrawer.classList.add('active');
              document.body.classList.add('overflow-hidden');
            }
          }
        }

        if (data['cart-icon-bubble']) {
          var newDoc2 = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
          var newBubble = newDoc2.querySelector('#cart-icon-bubble');
          var curBubble = document.querySelector('#cart-icon-bubble');
          if (curBubble && newBubble) curBubble.replaceWith(newBubble);
        }

        if (data['cart-live-region-text']) {
          var newDoc3 = parser.parseFromString(data['cart-live-region-text'], 'text/html');
          var newLive = newDoc3.querySelector('#shopify-section-cart-live-region-text');
          var curLive = document.querySelector('#shopify-section-cart-live-region-text');
          if (curLive && newLive) curLive.replaceWith(newLive);
        }

        // Standalone /cart page contents
        if (data['main-cart-items']) {
          var newDoc4 = parser.parseFromString(data['main-cart-items'], 'text/html');
          var newCart = newDoc4.querySelector('#main-cart-items');
          var curCart = document.querySelector('#main-cart-items');
          if (curCart && newCart) curCart.replaceWith(newCart);
        }
        if (data['main-cart-footer']) {
          var newDoc5 = parser.parseFromString(data['main-cart-footer'], 'text/html');
          var newFooter = newDoc5.querySelector('#main-cart-footer');
          var curFooter = document.querySelector('#main-cart-footer');
          if (curFooter && newFooter) curFooter.replaceWith(newFooter);
        }
      })
      .catch(function () {});
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) { productCache[handle] = data; return data; });
  }

  function countBundleItems(cart, productType) {
    if (!cart || !cart.items || !productType) return 0;
    var t = String(productType).toLowerCase();
    return cart.items.reduce(function (sum, item) {
      if (String(item.product_type || '').toLowerCase() === t) return sum + (item.quantity || 0);
      return sum;
    }, 0);
  }

  function computeRemainder(count, size) {
    size = size || 3;
    if (count <= 0) return { needed: size, nextMultiple: size, percent: 0, complete: false };
    // Once you have at least one full bundle, the deal is considered unlocked.
    if (count >= size) {
      var overflow = count % size;
      return {
        needed: overflow === 0 ? 0 : (size - overflow),
        nextMultiple: Math.ceil(count / size) * size,
        percent: 100,
        complete: true
      };
    }
    return {
      needed: size - count,
      nextMultiple: size,
      percent: Math.round((count / size) * 100),
      complete: false
    };
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
    var toggle = document.querySelector('[data-cart-toggle], .js-drawer-open-cart, .header__icon--cart, .site-header__cart');
    if (toggle) toggle.click();
  }

  function refreshReminders() {
    var bars = document.querySelectorAll('[data-cart-bundle-reminder]');
    if (!bars.length) return Promise.resolve();
    return getCart().then(function (cart) {
      bars.forEach(function (bar) {
        var type = bar.getAttribute('data-bundle-type') || 'Shorts';
        var size = parseInt(bar.getAttribute('data-bundle-size'), 10) || 3;
        var savings = parseInt(bar.getAttribute('data-bundle-savings'), 10) || 44;
        var count = countBundleItems(cart, type);
        updateBar(bar, count, size, savings);
      });
      emit('reminder:refreshed');
    }).catch(function () { /* silent */ });
  }

  function updateBar(bar, count, size, savings) {
    bar.setAttribute('data-shorts-count', count);
    var msgEl = bar.querySelector('[data-reminder-msg]');
    var badgeEl = bar.querySelector('[data-reminder-badge]');
    var fillEl = bar.querySelector('[data-reminder-fill]');
    var progressEl = bar.querySelector('[data-reminder-progress]');
    var pickEl = bar.querySelector('[data-reminder-pick]');
    var iconEl = bar.querySelector('[data-reminder-icon]');

    bar.classList.remove('is-hidden', 'is-complete', 'is-partial');

    if (count <= 0) {
      bar.classList.add('is-hidden');
      return;
    }

    var r = computeRemainder(count, size);

    if (r.complete) {
      bar.classList.add('is-complete');
      if (msgEl) msgEl.innerHTML = '<strong>Bundle unlocked</strong> — saving $' + savings + ' at checkout';
      if (badgeEl) badgeEl.textContent = 'UNLOCKED';
      if (progressEl) progressEl.style.display = 'none';
      if (pickEl) pickEl.style.display = 'none';
      if (iconEl) iconEl.style.display = '';
      return;
    }

    bar.classList.add('is-partial');
    if (iconEl) iconEl.style.display = 'none';
    if (progressEl) progressEl.style.display = '';
    if (fillEl) fillEl.style.width = r.percent + '%';
    if (pickEl) pickEl.style.display = '';

    var isOneAway = (r.needed === 1);
    if (badgeEl) badgeEl.textContent = isOneAway ? '1 AWAY' : (size + ' FOR $' + ((bar.getAttribute('data-bundle-price') || '100'))) ;
    if (msgEl) {
      var word = r.needed === 1 ? 'short' : 'shorts';
      msgEl.innerHTML = 'Add <strong>' + r.needed + ' more ' + word + '</strong> · save <strong>$' + savings + '</strong>' + (isOneAway ? ' instantly' : '');
    }
  }

  // Delegated handlers so they survive cart-drawer DOM replacement
  function bindReminderDelegation() {
    document.addEventListener('click', function (e) {
      // "Pick" in reminder bar -> expand picker in-place
      var pickBtn = e.target.closest('[data-reminder-pick]');
      if (pickBtn) {
        e.preventDefault();
        var bar = pickBtn.closest('[data-cart-bundle-reminder]');
        if (!bar) return;
        var picker = findAssociatedPicker(bar);
        if (!picker) return;
        openCartPicker(bar, picker);
        return;
      }

      // Back arrow inside picker
      var backBtn = e.target.closest('[data-picker-back]');
      if (backBtn) {
        e.preventDefault();
        var picker2 = backBtn.closest('[data-cart-bundle-picker]');
        if (!picker2) return;
        var bar2 = findAssociatedReminder(picker2);
        closeCartPicker(bar2, picker2);
        return;
      }

      // Tile click inside picker
      var tile = e.target.closest('[data-picker-tile]');
      if (tile) {
        if (e.target.closest('.cart-bundle-picker__size')) return; // sizes handled below
        e.preventDefault();
        var picker3 = tile.closest('[data-cart-bundle-picker]');
        openTileSizes(tile, picker3);
        return;
      }

    });

    // Escape closes any open picker
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('[data-cart-bundle-picker].is-open').forEach(function (picker) {
        var bar = findAssociatedReminder(picker);
        closeCartPicker(bar, picker);
      });
    });
  }

  function findAssociatedPicker(bar) {
    // Picker is a direct sibling after the reminder bar
    var sib = bar.nextElementSibling;
    while (sib) {
      if (sib.matches && sib.matches('[data-cart-bundle-picker]')) return sib;
      sib = sib.nextElementSibling;
    }
    return document.querySelector('[data-cart-bundle-picker]');
  }
  function findAssociatedReminder(picker) {
    var sib = picker.previousElementSibling;
    while (sib) {
      if (sib.matches && sib.matches('[data-cart-bundle-reminder]')) return sib;
      sib = sib.previousElementSibling;
    }
    return document.querySelector('[data-cart-bundle-reminder]');
  }

  function openCartPicker(bar, picker) {
    // Inline: hide cart content siblings while picker is shown, keep reminder bar visible above
    var host = (bar && bar.parentElement) || picker.parentElement;
    picker._hiddenNodes = [];
    if (host) {
      Array.prototype.forEach.call(host.children, function (node) {
        if (node === bar || node === picker) return;
        picker._hiddenNodes.push({ el: node, prev: node.style.display });
        node.style.display = 'none';
      });
    }
    picker.classList.add('is-open');
  }
  function closeCartPicker(bar, picker) {
    picker.classList.remove('is-open');
    picker.querySelectorAll('.cart-bundle-picker__tile.is-picking').forEach(function (t) { t.classList.remove('is-picking'); });
    (picker._hiddenNodes || []).forEach(function (x) { x.el.style.display = x.prev || ''; });
    picker._hiddenNodes = [];
  }

  function openTileSizes(tile, picker) {
    // Close other tiles
    picker.querySelectorAll('.cart-bundle-picker__tile.is-picking').forEach(function (t) {
      if (t !== tile) t.classList.remove('is-picking');
    });
    if (tile.classList.contains('is-picking')) { tile.classList.remove('is-picking'); return; }
    tile.classList.add('is-picking');

    var sizesEl = tile.querySelector('[data-tile-sizes]');
    if (!sizesEl || sizesEl.dataset.loaded === '1') return;

    // Keep the heading, replace any previously rendered size buttons
    var heading = sizesEl.querySelector('.cart-bundle-picker__sizes-heading');
    sizesEl.innerHTML = '';
    if (heading) sizesEl.appendChild(heading);
    var loader = document.createElement('span');
    loader.style.cssText = 'font-size:11px;color:#8a8a8a;text-align:center;';
    loader.textContent = 'Loading…';
    sizesEl.appendChild(loader);

    var handle = tile.getAttribute('data-handle');
    fetchProduct(handle).then(function (p) {
      loader.remove();
      (p.variants || []).forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'cart-bundle-picker__size' + (v.available ? '' : ' oos');
        b.textContent = (v.title === 'Default Title' || !v.title) ? 'Add' : v.title;
        if (!v.available) {
          b.addEventListener('click', function (ev) { ev.preventDefault(); ev.stopPropagation(); });
        } else {
          b.addEventListener('click', function (ev) {
            ev.stopPropagation();
            b.disabled = true;
            b.textContent = 'Adding…';
            addItems([{ id: v.id, quantity: 1 }]).then(function () {
              var bar = findAssociatedReminder(picker);
              closeCartPicker(bar, picker);
            }).catch(function () {
              b.disabled = false;
              b.textContent = v.title || 'Add';
            });
          });
        }
        sizesEl.appendChild(b);
      });
      sizesEl.dataset.loaded = '1';
    }).catch(function () {
      loader.textContent = 'Error loading sizes';
      loader.style.color = '#b00020';
    });
  }

  // Listen for cart updates from Dawn and third-party themes
  function bindThemeCartEvents() {
    ['cart:refresh', 'cart:updated', 'cart-drawer:updated', 'cart:change'].forEach(function (ev) {
      document.addEventListener(ev, function () { refreshReminders(); });
    });
    // Fallback: observe cart drawer contents for mutations
    var drawer = document.querySelector('cart-drawer, #CartDrawer');
    if (drawer && typeof MutationObserver !== 'undefined') {
      var debounce;
      var obs = new MutationObserver(function () {
        clearTimeout(debounce);
        debounce = setTimeout(refreshReminders, 100);
      });
      obs.observe(drawer, { childList: true, subtree: true });
    }
  }

  window.FractureBundle = {
    addItems: addItems,
    getCart: getCart,
    fetchProduct: fetchProduct,
    countBundleItems: countBundleItems,
    computeRemainder: computeRemainder,
    refreshReminders: refreshReminders,
    refreshCartSections: refreshCartSections,
    openCartDrawer: openCartDrawer,
    formatMoney: formatMoney,
    on: on,
    off: off
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bindThemeCartEvents();
      bindReminderDelegation();
      refreshReminders();
    });
  } else {
    bindThemeCartEvents();
    bindReminderDelegation();
    refreshReminders();
  }
})();
